import { useEffect } from 'react'
import { Box, Divider, Flex } from '@mantine/core'
import { useForm, zodResolver } from '@mantine/form'
import { randomId } from '@mantine/hooks'
import { useIsMutating } from '@tanstack/react-query'
import dayjs from 'dayjs'

import { Button } from '@/components/Button/Button'
import { CheckboxInput } from '@/components/CheckboxInput/CheckboxInput'
import { Loader } from '@/components/Loader/Loader'
import { manualAccountFormSchema } from '@/components/ManualAccountForm/ManualAccountForm.schema'
import type {
  ManualAccountFormProps,
  ManualAccountFormSchema,
} from '@/components/ManualAccountForm/ManualAccountForm.types'
import { NumberInput } from '@/components/NumberInput/NumberInput'
import { SelectInput } from '@/components/SelectInput/SelectInput'
import { SeparateDateInput } from '@/components/SeparateDateInput/SeparateDateInput'
import { Text } from '@/components/Text/Text'
import { TextInput } from '@/components/TextInput/TextInput'
import { Title } from '@/components/Title/Title'
import { ValueChangeItem } from '@/components/ValueChangeItem/ValueChangeItem'

import {
  ESTIMATION_METHOD_OPTIONS,
  MANUAL_ACCOUNT_OPTIONS,
} from '@/constants/manualAccount.constants'

import {
  estimationMethod,
  type as ManualAccountType,
} from '@/__generated__/requests'

export function ManualAccountForm({
  data,
  edit,
  isLoading = false,
  onClose,
  onSubmit,
}: ManualAccountFormProps) {
  const form = useForm<ManualAccountFormSchema>({
    initialValues: data,
    validate: zodResolver(manualAccountFormSchema),
  })

  const isMutating = useIsMutating({
    exact: true,
  })

  const isOtherAccountType =
    form.values.type === ManualAccountType.OTHER_LIABILITY ||
    form.values.type === ManualAccountType.OTHER_ASSET

  const handleUpdateDate = (value: Date | undefined, key: string) => {
    form.setFieldValue(`${key}`, value ? value.toISOString() : undefined)
  }

  const handleSubmit = form.onSubmit(values => onSubmit(values))

  useEffect(() => {
    if (data && edit) {
      form.setValues(data)
      form.resetDirty(data)
    } else {
      form.setFieldValue('valueChanged', false)
      form.setFieldValue('includedInNetWorth', false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  return (
    <>
      <Title mb={8} variant="h1">
        {!edit ? 'Add account' : 'Edit account'}
      </Title>

      <Text>
        Manual tracking requires you to update this balance whenever changes
        occur to ensure accurate financial information.
      </Text>

      {isLoading ? (
        <Loader isMinimized />
      ) : (
        <Box component="form" mt={32} noValidate onSubmit={handleSubmit}>
          <TextInput
            label="Institution Name"
            {...form.getInputProps('institution')}
          />

          <TextInput label="Account Name" {...form.getInputProps('name')} />

          <NumberInput
            hideControls
            label="Current Balance"
            {...form.getInputProps('balance')}
          />

          <SeparateDateInput
            disableFutureDates
            label="Balance Date"
            onChange={value =>
              handleUpdateDate(
                value instanceof Date ? value : undefined,
                'balanceDate',
              )
            }
            value={
              form.values.balanceDate
                ? dayjs(form.values.balanceDate).toDate()
                : undefined
            }
          />

          <SelectInput
            {...form.getInputProps('type')}
            data={MANUAL_ACCOUNT_OPTIONS}
            label="Type of Account"
            placeholder="Select account type"
          />

          {isOtherAccountType && (
            <TextInput
              label="Other Type"
              {...form.getInputProps('otherType')}
            />
          )}

          <NumberInput
            hideControls
            label="Initial Value"
            {...form.getInputProps('initialValue')}
            onChange={value =>
              form.setFieldValue(
                'initialValue',
                typeof value === 'number' ? value : null,
              )
            }
          />

          <SeparateDateInput
            disableFutureDates
            label="Initial value date"
            onChange={value =>
              handleUpdateDate(
                value instanceof Date ? value : undefined,
                'initialValueDate',
              )
            }
            value={
              form.values.initialValueDate
                ? dayjs(form.values.initialValueDate).toDate()
                : undefined
            }
          />

          <CheckboxInput
            {...form.getInputProps('valueChanged')}
            label="Has this value changed over time?"
            onChange={event => {
              if (!event.currentTarget.checked) {
                form.setFieldValue('valueChanges', [])
              }

              form.setFieldValue('valueChanged', event.currentTarget.checked)
            }}
          />

          {form.values.valueChanged && (
            <>
              {form.values.valueChanges?.map((change, index) => (
                <ValueChangeItem key={change.id} form={form} index={index} />
              ))}
              <Button
                mt={16}
                onClick={() => {
                  form.insertListItem('valueChanges', {
                    date: undefined,
                    id: randomId(),
                    value: '',
                  })
                }}
                variant="text"
              >
                Add another entry
              </Button>

              <Divider mt={16} />
            </>
          )}

          <SelectInput
            {...form.getInputProps('estimationMethod')}
            data={ESTIMATION_METHOD_OPTIONS}
            label="Estimation Method"
            mt={16}
            placeholder="Select estimation method"
          />

          {form.values.estimationMethod === estimationMethod.OTHER && (
            <TextInput
              label="Other Estimation Method"
              {...form.getInputProps('otherEstimationMethod')}
            />
          )}

          <TextInput label="Notes" {...form.getInputProps('notes')} />

          <CheckboxInput
            {...form.getInputProps('includedInNetWorth')}
            label="Include this account in my net worth"
          />

          <Flex
            direction={{ base: 'column', md: 'row' }}
            gap={16}
            justify={{ base: 'center', md: 'flex-start' }}
            mt={32}
          >
            <Button miw="48%" onClick={onClose} variant="outline">
              Cancel
            </Button>

            <Button loading={!!isMutating} miw="48%" type="submit">
              {!edit ? 'Add account' : 'Edit account'}
            </Button>
          </Flex>
        </Box>
      )}
    </>
  )
}
